import React, { useEffect, useState } from 'react';
import Row from '@amzn/meridian/row';
import Textarea from '@amzn/meridian/textarea';
import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';

import AtAcrossZoneBulkUpload from '@components/agile/agileComponents/AtAcrossZoneOperation/AtAcrossZoneComponents/AtAcrossZoneBulkUpload';
import AtAcrossZoneDiffTable from '@components/agile/agileComponents/AtAcrossZoneOperation/AtAcrossZoneComponents/AtAcrossZoneDiffTable';
import { InputAsinsValidationUtils, getAsinValidationMessage } from '@components/agile/utils/InputAsinsValidationUtils';
import Button from '@amzn/meridian/button';
import { AgileMacroplanogram } from '@components/agile/types/AgileMacroplanogramTypes';
import AtAcrossZoneRecommedationTable from '@components/agile/agileComponents/AtAcrossZoneOperation/AtAcrossZoneComponents/AtAcrossZoneRecommedationTable';
import { AgileBatchRecommedationInput } from '@components/agile/types/AgileRecommendationTypes';
import { getActionPublisherForProgram, publishKatalMetric } from 'src/metrics/publishers';
import {
  METHOD_NAME_BULK_UPLOAD,
  METRIC_NAME_GENERATE_DIFF,
  METRIC_NAME_GO_TO_BULK_UPLOAD,
  MetricType,
} from 'src/constants/metricConstants';
import AgileUploadRow from '../../types/AgileUploadRow';

export interface AtAcrossZoneMainProps {
  listOfMacroplanogram: AgileMacroplanogram[] | undefined;
  swapTime: number;
}
const AtAcrossZoneMain = (props: AtAcrossZoneMainProps) => {
  const { listOfMacroplanogram, swapTime } = props;
  const [inputAsins, setInputAsins] = useState<string[]>([]);
  const [textAreaInput, setTextAreaInput] = useState<string>('');
  const [enableTextArea, setEnableTextArea] = useState<boolean>(false);
  const [textAreaError, setTextAreaError] = useState<boolean>(false);
  const [disableButton, setDisableButton] = useState<boolean>(true);
  const [inputTableFlag, setInputTableFlag] = useState(false);
  const [alertFlag, setAlertFlag] = useState<boolean>(false);
  const [zoneNotFoundAlertFlag, setZoneNotFoundAlertFlag] = useState<boolean>(false);
  const [recommendationInput, setRecommendationInput] = useState<AgileBatchRecommedationInput[]>([]);
  const [updatedRecommendInputWithDiffData, setUpdatedRecommendInputWithDiffData] = useState<
    AgileBatchRecommedationInput[]
  >([]);
  const [recommendationFlag, setRecommendationFlag] = useState<boolean>(false);
  const [diffTableData, setDiffTableData] = useState<AgileUploadRow[]>([]);
  const [macroplanogramToFacingUnitsMap, setMacroplanogramToFacingUnitsMap] = useState<{
    [macroplanogramId: string]: number;
  }>({});
  const [macroplanogramToSubcategoryFacingUnitsMap, setMacroplanogramToSubcategoryFacingUnitsMap] = useState<{
    [macroplanogramId: string]: { [subcategoryId: string]: number };
  }>({});

  useEffect(() => {
    publishKatalMetric(
      getActionPublisherForProgram(METHOD_NAME_BULK_UPLOAD),
      METRIC_NAME_GO_TO_BULK_UPLOAD,
      1,
      MetricType.Counter,
    );
  }, []);

  useEffect(() => {
    const asinList = Array.from(new Set(textAreaInput.split(/[,\s]+/)));
    const asinValidationFlag = !InputAsinsValidationUtils(asinList);
    if (!asinValidationFlag) setInputAsins(asinList);
    setTextAreaError(getAsinValidationMessage(asinList));
    setDisableButton(asinValidationFlag);
    setRecommendationFlag(false);
    setInputTableFlag(false);
  }, [textAreaInput]);

  useEffect(() => {
    const isListOfMacroplanogramValid = listOfMacroplanogram !== undefined && listOfMacroplanogram !== null;
    const isListOfMacroplanogramEmpty = Array.isArray(listOfMacroplanogram) && listOfMacroplanogram.length === 0;
    setAlertFlag(!isListOfMacroplanogramValid);
    setInputTableFlag(false);
    setZoneNotFoundAlertFlag(isListOfMacroplanogramEmpty);
    setRecommendationFlag(false);
  }, [listOfMacroplanogram]);

  const onClick = () => {
    setInputTableFlag(false);
    setRecommendationFlag(false);
    const isListOfMacroplanogramValid = listOfMacroplanogram !== undefined && listOfMacroplanogram !== null;
    setAlertFlag(!isListOfMacroplanogramValid);
    publishKatalMetric(
      getActionPublisherForProgram(METHOD_NAME_BULK_UPLOAD),
      METRIC_NAME_GENERATE_DIFF,
      1,
      MetricType.Counter,
    );
    setTimeout(() => {
      setInputTableFlag(isListOfMacroplanogramValid);
    }, 500);
  };

  return (
    <>
      {alertFlag && (
        <Alert type="error" onClose={() => setAlertFlag(false)}>
          Please select a store and a live date before continuing.
        </Alert>
      )}
      {zoneNotFoundAlertFlag && (
        <Alert type="error" onClose={() => setZoneNotFoundAlertFlag(false)}>
          The selected store does not contain any zones for the chosen date. Please create zones before proceeding.
        </Alert>
      )}
      <Column alignmentHorizontal="center" spacingInset="200">
        <Row alignmentHorizontal="center">
          <Textarea
            value={textAreaInput}
            onChange={setTextAreaInput}
            placeholder="Enter a list of ASINs that you would like to add across zones"
            error={textAreaError}
            resize="auto"
            rows={5}
            disabled={enableTextArea}
            errorMessage={textAreaError && !enableTextArea ? 'Invalid List of Asins' : null}
            helperText="For multiple ASINs use commas (,) eg: B00XXXXXFW, B00XXXXXF6"
            width={500}
          />
          <Column alignmentHorizontal="center" spacingInset="200">
            <AtAcrossZoneBulkUpload
              setEnableTextArea={setEnableTextArea}
              setInputAsins={setInputAsins}
              setDisableButton={setDisableButton}
              setInputTableFlag={setInputTableFlag}
              setRecommendationFlag={setRecommendationFlag}
              setTextAreaInput={setTextAreaInput}
            />
          </Column>
        </Row>
      </Column>

      <Row width="100%" alignmentHorizontal="center" spacingInset="300" backgroundColor="subdued">
        <Button disabled={disableButton} onClick={onClick}>
          Next
        </Button>
      </Row>
      {inputTableFlag && (
        <AtAcrossZoneDiffTable
          listOfMacroplanogram={listOfMacroplanogram}
          swapTime={swapTime}
          inputAsins={inputAsins}
          setRecommendationInput={setRecommendationInput}
          setRecommendationFlag={setRecommendationFlag}
          setDiffTableData={setDiffTableData}
          setMacroplanogramToFacingUnitsMap={setMacroplanogramToFacingUnitsMap}
          setMacroplanogramToSubcategoryFacingUnitsMap={setMacroplanogramToSubcategoryFacingUnitsMap}
          setUpdatedRecommendInputWithDiffData={setUpdatedRecommendInputWithDiffData}
        />
      )}
      {recommendationFlag && (
        <AtAcrossZoneRecommedationTable
          recommendationInput={recommendationInput}
          swapTime={swapTime}
          listOfMacroplanogram={listOfMacroplanogram}
          diffTableData={diffTableData}
          macroplanogramToFacingUnitsMap={macroplanogramToFacingUnitsMap}
          macroplanogramToSubcategoryFacingUnitsMap={macroplanogramToSubcategoryFacingUnitsMap}
          setInputTableFlag={setInputTableFlag}
          setRecommendationFlag={setRecommendationFlag}
          updatedRecommendInputWithDiffData={updatedRecommendInputWithDiffData}
        />
      )}
    </>
  );
};

export default AtAcrossZoneMain;
